/**
 * PLANS MANAGEMENT
 *
 * Manage subscription plans with advanced pricing options:
 * - Fixed monthly or per-employee billing
 * - Time-based pricing phases (free trial, discounts)
 * - Employee count thresholds (e.g., first 40 free)
 *
 * Example: 3 months free → 3 months €100 → €150 + €2/employee over 40
 *
 * Configuration:
 * - Billing Model: PER_EMPLOYEE
 * - Base Price: 150
 * - Price Per Employee: 2
 * - Included Employees: 40
 * - Pricing Phases:
 *   [0→3]: 0€ (Free trial)
 *   [3→6]: 100€ (Discount)
 *   [6→∞]: 150€ (Standard + employee charges)
 */

import React, { useState, useEffect } from "react";
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  InputNumber,
  Tag,
  Space,
  message,
  Popconfirm,
  Switch,
  Card,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  MinusCircleOutlined,
} from "@ant-design/icons";
import { subscriptionAdminAPI } from "../../../utils/api/subscriptionAPI";
import "./PlansManagement.scss";

const { TextArea } = Input;

interface PricingPhase {
  fromMonth: number;
  toMonth: number | null;
  price: number;
  description: string;
}

interface Plan {
  _id: string;
  name: string;
  billingModel: "FIXED_MONTHLY" | "PER_EMPLOYEE";
  basePrice: number;
  pricePerEmployee: number;
  includedEmployees?: number;
  pricingPhases?: PricingPhase[];
  currency: string;
  paymentTermDays: number;
  gracePeriodDays: number;
  features: string[];
  maxEmployees: number | null;
  description: string;
  isActive: boolean;
  createdAt: string;
}

const AVAILABLE_FEATURES = [
  "appointments",
  "calendar",
  "dashboard",
  "leaves",
  "overtime",
  "patientProfile",
  "patients",
  "business",
  "investments",
  "dailyRevenue",
  "payments",
  "schedules",
  "settings",
  "shifts",
  "staff",
  "support",
  "userProfile",
];

const PlansManagement: React.FC = () => {
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    setLoading(true);
    try {
      const response = await subscriptionAdminAPI.getAllPlans();
      if (response.data && response.data.data) {
        const plansData = Array.isArray(response.data.data)
          ? response.data.data
          : [];
        setPlans(plansData);
      }
    } catch (error) {
      console.error("Error fetching plans:", error);
      message.error("Failed to load plans");
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePlan = () => {
    setSelectedPlan(null);
    form.resetFields();
    form.setFieldsValue({
      currency: "EUR",
      paymentTermDays: 15,
      gracePeriodDays: 5,
      isActive: true,
      billingModel: "FIXED_MONTHLY",
    });
    setIsModalVisible(true);
  };

  const handleEditPlan = (plan: Plan) => {
    setSelectedPlan(plan);
    form.setFieldsValue({
      ...plan,
      maxEmployees: plan.maxEmployees || undefined,
    });
    setIsModalVisible(true);
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();

      if (selectedPlan) {
        // Update existing plan
        await subscriptionAdminAPI.updatePlan(selectedPlan._id, values);
        message.success("Plan updated successfully!");
      } else {
        // Create new plan
        await subscriptionAdminAPI.createPlan(values);
        message.success("Plan created successfully!");
      }

      setIsModalVisible(false);
      form.resetFields();
      fetchPlans();
    } catch (error: any) {
      console.error("Error saving plan:", error);
      message.error(error.response?.data?.message || "Failed to save plan");
    }
  };

  const handleDeactivatePlan = async (planId: string) => {
    try {
      await subscriptionAdminAPI.deactivatePlan(planId);
      message.success("Plan deactivated successfully!");
      fetchPlans();
    } catch (error: any) {
      message.error("Failed to deactivate plan");
    }
  };

  const handleDeletePlan = async (planId: string) => {
    try {
      await subscriptionAdminAPI.deletePlan(planId);
      message.success("Plan permanently deleted!");
      fetchPlans();
    } catch (error: any) {
      message.error(error.response?.data?.message || "Failed to delete plan");
    }
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (name: string, record: Plan) => (
        <Space direction="vertical" size={0}>
          <strong>{name}</strong>
          <small style={{ color: "#666" }}>{record.description}</small>
        </Space>
      ),
    },
    {
      title: "Billing Model",
      dataIndex: "billingModel",
      key: "billingModel",
      render: (model: string) => (
        <Tag color={model === "FIXED_MONTHLY" ? "blue" : "green"}>
          {model === "FIXED_MONTHLY" ? "Fixed Monthly" : "Per Employee"}
        </Tag>
      ),
    },
    {
      title: "Pricing",
      key: "pricing",
      render: (_: any, record: Plan) => (
        <Space direction="vertical" size={0}>
          {record.pricingPhases && record.pricingPhases.length > 0 ? (
            <>
              <Tag color="orange">{record.pricingPhases.length} phases</Tag>
              {record.billingModel === "PER_EMPLOYEE" && (
                <small>
                  + €{record.pricePerEmployee}/emp
                  {record.includedEmployees
                    ? ` (>${record.includedEmployees})`
                    : ""}
                </small>
              )}
            </>
          ) : record.billingModel === "FIXED_MONTHLY" ? (
            <span>€{record.basePrice}/month</span>
          ) : (
            <>
              <span>Base: €{record.basePrice}</span>
              <small>
                + €{record.pricePerEmployee}/emp
                {record.includedEmployees
                  ? ` (>${record.includedEmployees})`
                  : ""}
              </small>
            </>
          )}
        </Space>
      ),
    },
    {
      title: "Max Employees",
      dataIndex: "maxEmployees",
      key: "maxEmployees",
      align: "center" as const,
      render: (max: number | null) => max || "∞",
    },
    {
      title: "Features",
      dataIndex: "features",
      key: "features",
      render: (features: string[]) => (
        <Tag color="purple">{features.length} features</Tag>
      ),
    },
    {
      title: "Payment Terms",
      key: "terms",
      render: (_: any, record: Plan) => (
        <Space direction="vertical" size={0}>
          <small>Due: {record.paymentTermDays} days</small>
          <small>Grace: {record.gracePeriodDays} days</small>
        </Space>
      ),
    },
    {
      title: "Status",
      dataIndex: "isActive",
      key: "isActive",
      render: (isActive: boolean) => (
        <Tag color={isActive ? "green" : "red"}>
          {isActive ? "Active" : "Inactive"}
        </Tag>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_: any, record: Plan) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEditPlan(record)}
          >
            Edit
          </Button>
          <Popconfirm
            title="Deactivate this plan?"
            description="Existing subscriptions won't be affected"
            onConfirm={() => handleDeactivatePlan(record._id)}
          >
            <Button type="link" icon={<DeleteOutlined />}>
              Deactivate
            </Button>
          </Popconfirm>
          <Popconfirm
            title="Permanently delete this plan?"
            description="This action cannot be undone. Plan must not be used by any subscriptions."
            onConfirm={() => handleDeletePlan(record._id)}
            okText="Delete"
            okType="danger"
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const billingModelValue = Form.useWatch("billingModel", form);

  return (
    <div className="plans-management">
      <Card>
        <div className="page-header">
          <div>
            <h1>Subscription Plans</h1>
            <p>Manage pricing plans for your subscription system</p>
          </div>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleCreatePlan}
          >
            Create Plan
          </Button>
        </div>

        <Table
          columns={columns}
          dataSource={plans}
          loading={loading}
          rowKey="_id"
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} plans`,
          }}
        />
      </Card>

      <Modal
        title={selectedPlan ? "Edit Plan" : "Create New Plan"}
        open={isModalVisible}
        onOk={handleModalOk}
        onCancel={() => {
          setIsModalVisible(false);
          form.resetFields();
        }}
        width={700}
        okText={selectedPlan ? "Update" : "Create"}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="name"
            label="Plan Name"
            rules={[{ required: true, message: "Please enter plan name" }]}
          >
            <Input placeholder="e.g., Professional" />
          </Form.Item>

          <Form.Item
            name="description"
            label="Description"
            rules={[{ required: true, message: "Please enter description" }]}
          >
            <TextArea rows={2} placeholder="Brief description of this plan" />
          </Form.Item>

          <Form.Item
            name="billingModel"
            label="Billing Model"
            rules={[{ required: true }]}
          >
            <Select>
              <Select.Option value="FIXED_MONTHLY">
                Fixed Monthly Price
              </Select.Option>
              <Select.Option value="PER_EMPLOYEE">
                Base Price + Per Employee
              </Select.Option>
            </Select>
          </Form.Item>

          <Space style={{ width: "100%" }} size="large">
            <Form.Item
              name="basePrice"
              label="Base Price (€)"
              rules={[{ required: true, message: "Required" }]}
              style={{ marginBottom: 0, flex: 1 }}
            >
              <InputNumber min={0} style={{ width: "100%" }} placeholder="0" />
            </Form.Item>

            {billingModelValue === "PER_EMPLOYEE" && (
              <>
                <Form.Item
                  name="includedEmployees"
                  label="Included Employees"
                  tooltip="Number of employees included in base price (e.g., first 40 free)"
                  style={{ marginBottom: 0, flex: 1 }}
                >
                  <InputNumber
                    min={0}
                    style={{ width: "100%" }}
                    placeholder="0"
                  />
                </Form.Item>

                <Form.Item
                  name="pricePerEmployee"
                  label="Price per Employee (€)"
                  tooltip="Price for each employee above the included count"
                  rules={[{ required: true, message: "Required" }]}
                  style={{ marginBottom: 0, flex: 1 }}
                >
                  <InputNumber
                    min={0}
                    style={{ width: "100%" }}
                    placeholder="0"
                  />
                </Form.Item>
              </>
            )}
          </Space>

          <Form.Item label="Pricing Phases" style={{ marginTop: 16 }}>
            <div style={{ marginBottom: 8, color: "#666", fontSize: "12px" }}>
              Define time-based pricing (e.g., free trial, discounts). Leave
              empty to use base price always.
            </div>
            <Form.List name="pricingPhases">
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name, ...restField }) => (
                    <Space
                      key={key}
                      style={{ display: "flex", marginBottom: 8 }}
                      align="baseline"
                    >
                      <Form.Item
                        {...restField}
                        name={[name, "fromMonth"]}
                        rules={[{ required: true, message: "Required" }]}
                        style={{ marginBottom: 0, width: 80 }}
                      >
                        <InputNumber min={0} placeholder="From" />
                      </Form.Item>

                      <span>→</span>

                      <Form.Item
                        {...restField}
                        name={[name, "toMonth"]}
                        style={{ marginBottom: 0, width: 80 }}
                      >
                        <InputNumber min={0} placeholder="To (∞)" />
                      </Form.Item>

                      <Form.Item
                        {...restField}
                        name={[name, "price"]}
                        rules={[{ required: true, message: "Required" }]}
                        style={{ marginBottom: 0, width: 100 }}
                      >
                        <InputNumber
                          min={0}
                          placeholder="Price"
                          addonAfter="€"
                        />
                      </Form.Item>

                      <Form.Item
                        {...restField}
                        name={[name, "description"]}
                        style={{ marginBottom: 0, flex: 1 }}
                      >
                        <Input placeholder="Description (optional)" />
                      </Form.Item>

                      <MinusCircleOutlined
                        onClick={() => remove(name)}
                        style={{ color: "red", cursor: "pointer" }}
                      />
                    </Space>
                  ))}
                  <Button
                    type="dashed"
                    onClick={() => add()}
                    block
                    icon={<PlusOutlined />}
                  >
                    Add Pricing Phase
                  </Button>
                </>
              )}
            </Form.List>
          </Form.Item>

          <Space style={{ width: "100%", marginTop: 16 }} size="large">
            <Form.Item
              name="paymentTermDays"
              label="Payment Term (days)"
              rules={[{ required: true }]}
              style={{ marginBottom: 0, flex: 1 }}
            >
              <InputNumber min={1} max={90} style={{ width: "100%" }} />
            </Form.Item>

            <Form.Item
              name="gracePeriodDays"
              label="Grace Period (days)"
              rules={[{ required: true }]}
              style={{ marginBottom: 0, flex: 1 }}
            >
              <InputNumber min={0} max={30} style={{ width: "100%" }} />
            </Form.Item>

            <Form.Item
              name="maxEmployees"
              label="Max Employees"
              style={{ marginBottom: 0, flex: 1 }}
            >
              <InputNumber
                min={1}
                style={{ width: "100%" }}
                placeholder="∞ (no limit)"
              />
            </Form.Item>
          </Space>

          <Form.Item
            name="features"
            label="Included Features"
            rules={[{ required: true, message: "Select at least one feature" }]}
            style={{ marginTop: 16 }}
          >
            <Select
              mode="multiple"
              placeholder="Select features"
              options={AVAILABLE_FEATURES.map((f) => ({ label: f, value: f }))}
            />
          </Form.Item>

          <Form.Item
            name="currency"
            label="Currency"
            rules={[{ required: true }]}
          >
            <Select>
              <Select.Option value="EUR">EUR (€)</Select.Option>
              <Select.Option value="USD">USD ($)</Select.Option>
              <Select.Option value="GBP">GBP (£)</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="isActive"
            label="Active Status"
            valuePropName="checked"
          >
            <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default PlansManagement;
